import { StoragePoolSelect } from "@/admin/buckets/StoragePoolSelect.tsx";
import { AInput } from "@/admin/form/Fields.tsx";
import type { AdminHostPoolWithType } from "@/admin/host_pools/hostPoolsQuery.ts";
import { hostPoolCreateMutation, hostPoolUpdateMutation } from "@/admin/host_pools/hostPoolsQuery.ts";
import { controllerProps, formPropsToAriaProps } from "@/components/forms/ariaController.tsx";
import { FormField } from "@/components/forms/FormField.tsx";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { WNumberField } from "@/components/forms/WNumberField.tsx";
import { WTextField } from "@/components/forms/WTextField.tsx";
import { WModal } from "@/components/modal/WModal.tsx";
import { InfoTooltip } from "@/components/WTooltip.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { useLocationsForType } from "@/modules/location/locationQuery.ts";
import { LocationSelect } from "@/modules/location/LocationSelect.tsx";
import type { ReactNode } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";

export function AddHostPoolButton({ button }: { button: ReactNode }) {
    return (
        <WModal button={button}>
            <AddEditHostPoolModalContent />
        </WModal>
    );
}

export function EditHostPoolButton({ item, button }: { item: AdminHostPoolWithType; button: ReactNode }) {
    return (
        <WModal button={button}>
            <AddEditHostPoolModalContent item={item} />
        </WModal>
    );
}

function validateMinMax(min: number, max: number) {
    return max >= min ? undefined : "Max must be greater than min";
}

interface AddEditHostPoolInputs {
    location: string;
    name: string;
    description: string;
    cpu_allocate_coefficient: number;
    mem_allocate_coefficient: number;
    cpu_model: string;
    storage_pool_uuid: string;
    ui_position: number;
    cpu_min: number;
    cpu_max: number;
    ram_min: number;
    ram_max: number;
    disk_min: number;
    disk_max: number;
}

function AddEditHostPoolModalContent({ item }: { item?: AdminHostPoolWithType }) {
    const createMutation = useStandardMutation(hostPoolCreateMutation);
    const updateMutation = useStandardMutation(hostPoolUpdateMutation);
    const locations = useLocationsForType("virtual_machine");

    const isEditMode = !!item;

    const onSubmit: SubmitHandler<AddEditHostPoolInputs> = async (inputs) => {
        const {
            location,
            name,
            description,
            cpu_allocate_coefficient,
            mem_allocate_coefficient,
            cpu_model,
            storage_pool_uuid,
            ui_position,
            cpu_min,
            cpu_max,
            ram_min,
            ram_max,
            disk_min,
            disk_max,
        } = inputs;

        const guestLimits = {
            cpu: { min: cpu_min, max: cpu_max },
            ram_mb: { min: ram_min, max: ram_max },
            disk_gb: { min: disk_min, max: disk_max },
        };

        if (isEditMode) {
            await updateMutation.mutateAsync({
                body: {
                    name,
                    description,
                    cpu_model,
                    cpu_allocate_coefficient,
                    mem_allocate_coefficient,
                    storage_pool_uuid,
                    ui_position: Number(ui_position),
                    uuid: item.uuid,
                    guest_limits: guestLimits,
                },
                location,
                uuid: item.uuid,
            });
        } else {
            await createMutation.mutateAsync({
                body: {
                    name,
                    description,
                    cpu_model,
                    cpu_allocate_coefficient,
                    mem_allocate_coefficient,
                    storage_pool_uuid,
                    guest_limits: guestLimits,
                },
                location,
            });
        }
    };

    const form = useForm<AddEditHostPoolInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            location: item?.location ?? locations.defaultLocation,
            name: item?.name ?? "",
            description: item?.description ?? "",
            cpu_allocate_coefficient: item?.cpu_allocate_coefficient ?? 2.5,
            mem_allocate_coefficient: item?.mem_allocate_coefficient ?? 0.8,
            cpu_model: item?.cpu_model ?? "",
            storage_pool_uuid: item?.storage_pool_uuid ?? "",
            ui_position: item?.ui_position ?? 0,
            cpu_min: item?.guest_limits?.cpu.min ?? 1,
            cpu_max: item?.guest_limits?.cpu.max ?? 16,
            ram_min: item?.guest_limits?.ram_mb.min ?? 1024,
            ram_max: item?.guest_limits?.ram_mb.max ?? 32768,
            disk_min: item?.guest_limits?.disk_gb.min ?? 10,
            disk_max: item?.guest_limits?.disk_gb.max ?? 500,
        },
    });

    const { control, watch } = form;

    return (
        <WModalForm
            title={isEditMode ? "Edit Hypervisor Pool" : "Add Hypervisor Pool"}
            label={isEditMode ? "Edit" : "Add"}
            form={form}
            onSubmit={onSubmit}
        >
            <Controller
                control={control}
                name="location"
                {...controllerProps({ isRequired: true })}
                render={({ field }) => (
                    <FormField wide headless label="Location">
                        <LocationSelect
                            resourceType="virtual_machine"
                            valueKey={field.value}
                            onChange={(item) => field.onChange(item.slug)}
                        />
                    </FormField>
                )}
            />

            <Controller
                control={control}
                name="name"
                {...controllerProps({ isRequired: true })}
                render={(props) => (
                    <WTextField isRequired wide headless label="Pool name" {...formPropsToAriaProps(props)}>
                        <AInput />
                    </WTextField>
                )}
            />

            <Controller
                control={control}
                name="description"
                render={(props) => (
                    <WTextField wide headless label="Description" {...formPropsToAriaProps(props)}>
                        <AInput />
                    </WTextField>
                )}
            />

            <Controller
                control={control}
                name="cpu_allocate_coefficient"
                {...controllerProps({ isRequired: true })}
                render={(props) => (
                    <WNumberField
                        autoFocus
                        headless
                        wide
                        label={
                            <div className="inline-flex w-80%">
                                <span>CPU allocate coefficient</span>
                                <InfoTooltip
                                    className="ml-1"
                                    text="The total number of vCPUs available to Virtual Machines on a host is calculated by multiplying the number of CPU threads on that host by this coefficient."
                                />
                            </div>
                        }
                        {...formPropsToAriaProps(props)}
                    >
                        <AInput type="number" min="0.01" max="5" step="0.01" />
                    </WNumberField>
                )}
            />

            <Controller
                control={control}
                name="mem_allocate_coefficient"
                {...controllerProps({ isRequired: true })}
                render={(props) => (
                    <WNumberField
                        autoFocus
                        headless
                        wide
                        label={
                            <div className="inline-flex w-80%">
                                <span>Mem allocate coefficient</span>
                                <InfoTooltip
                                    className="ml-1"
                                    text="The total amount of vRAM available to Virtual Machines on a host is calculated by multiplying the amount of total memory on that host by this coefficient."
                                />
                            </div>
                        }
                        {...formPropsToAriaProps(props)}
                    >
                        <AInput type="number" min="0.01" max="0.95" step="0.01" />
                    </WNumberField>
                )}
            />

            <Controller
                control={control}
                name="cpu_model"
                {...controllerProps({ isRequired: true })}
                render={(props) => (
                    <WTextField isRequired wide headless label="CPU mode" {...formPropsToAriaProps(props)}>
                        <AInput />
                    </WTextField>
                )}
            />

            <FormField wide headless label="CPU limits">
                <div className="flex gap-2 justify-between">
                    <div>
                        <Controller
                            control={control}
                            name="cpu_min"
                            {...controllerProps({ isRequired: true })}
                            render={(props) => (
                                <WNumberField
                                    className="w-30"
                                    headless
                                    aria-label="Min cpu"
                                    block
                                    minValue={1}
                                    step={1}
                                    {...formPropsToAriaProps(props)}
                                >
                                    <AInput type="number" />
                                </WNumberField>
                            )}
                        />
                    </div>

                    <div>
                        <Controller
                            control={control}
                            name="cpu_max"
                            {...controllerProps({ isRequired: true })}
                            rules={{
                                validate: (v, f) => {
                                    return validateMinMax(f.cpu_min, v);
                                },
                            }}
                            render={(props) => (
                                <WNumberField
                                    className="w-30"
                                    headless
                                    aria-label="Max cpu"
                                    block
                                    step={1}
                                    minValue={1}
                                    {...formPropsToAriaProps(props)}
                                >
                                    <AInput type="number" />
                                </WNumberField>
                            )}
                        />
                    </div>
                </div>
            </FormField>

            <FormField wide headless label="RAM limits (MB)">
                <div className="flex gap-2 justify-between">
                    <div>
                        <Controller
                            control={control}
                            name="ram_min"
                            {...controllerProps({ isRequired: true })}
                            render={(props) => (
                                <WNumberField
                                    className="w-30"
                                    headless
                                    aria-label="Min ram"
                                    block
                                    minValue={1}
                                    step={1}
                                    {...formPropsToAriaProps(props)}
                                >
                                    <AInput type="number" />
                                </WNumberField>
                            )}
                        />
                    </div>

                    <div>
                        <Controller
                            control={control}
                            name="ram_max"
                            {...controllerProps({ isRequired: true })}
                            rules={{
                                validate: (v, f) => {
                                    return validateMinMax(f.ram_min, v);
                                },
                            }}
                            render={(props) => (
                                <WNumberField
                                    className="w-30"
                                    headless
                                    aria-label="Max ram"
                                    block
                                    minValue={1}
                                    step={1}
                                    {...formPropsToAriaProps(props)}
                                >
                                    <AInput type="number" />
                                </WNumberField>
                            )}
                        />
                    </div>
                </div>
            </FormField>

            <FormField wide headless label="DISK limits (GB)">
                <div className="flex gap-2 justify-between">
                    <div>
                        <Controller
                            control={control}
                            name="disk_min"
                            {...controllerProps({ isRequired: true })}
                            render={(props) => (
                                <WNumberField
                                    className="w-30"
                                    headless
                                    block
                                    aria-label="Min disk"
                                    minValue={1}
                                    step={1}
                                    {...formPropsToAriaProps(props)}
                                >
                                    <AInput type="number" />
                                </WNumberField>
                            )}
                        />
                    </div>

                    <div>
                        <Controller
                            control={control}
                            name="disk_max"
                            {...controllerProps({ isRequired: true })}
                            rules={{
                                validate: (v, f) => {
                                    return validateMinMax(f.disk_min, v);
                                },
                            }}
                            render={(props) => (
                                <WNumberField
                                    className="w-30"
                                    headless
                                    block
                                    aria-label="Max disk"
                                    minValue={1}
                                    step={1}
                                    {...formPropsToAriaProps(props)}
                                >
                                    <AInput type="number" />
                                </WNumberField>
                            )}
                        />
                    </div>
                </div>
            </FormField>

            <Controller
                control={control}
                name="storage_pool_uuid"
                {...controllerProps({ isRequired: true })}
                render={({ field }) => (
                    <FormField isRequired wide headless label="Storage pool">
                        <StoragePoolSelect
                            valueKey={field.value}
                            location={watch("location")}
                            onChange={(item) => field.onChange(item.uuid)}
                        />
                    </FormField>
                )}
            />

            {isEditMode && (
                <Controller
                    control={control}
                    name="ui_position"
                    {...controllerProps({ isRequired: true })}
                    render={(props) => (
                        <WNumberField
                            wide
                            headless
                            label={
                                <div className="inline-flex w-80%">
                                    <span>UI position</span>
                                    <InfoTooltip
                                        className="ml-1"
                                        text="An integer that defines the order in which the pools are displayed for users. Smaller numbers come first."
                                    />
                                </div>
                            }
                            {...formPropsToAriaProps(props)}
                        >
                            <AInput />
                        </WNumberField>
                    )}
                />
            )}
        </WModalForm>
    );
}
